import React, { SetStateAction, useEffect, useRef } from 'react';
import { useMap } from 'hooks/useMap';
import { Geometry, LineString, MultiPolygon, Point, Polygon } from 'ol/geom';
import { Feature } from 'ol';
import GeoJSON from 'ol/format/GeoJSON';
import { Style, Circle, Fill } from 'ol/style';
import Stroke from 'ol/style/Stroke';
import VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import { useLayers } from 'hooks/useLayers';
import { IAtributo } from 'contexts/@types/Layers';
import { shouldApplyNotranslate } from 'utils/shouldApplyNoTranslate';
import { getFeature } from 'services/featureRequest';
import { isAnalytesAttribute } from 'utils/analytes';
import { getVectorLayerAttributes } from 'utils/vectorLayerAttributes';

interface IFeaturesModal {
  setIsOpen: React.Dispatch<SetStateAction<boolean>>;
  // eslint-disable-next-line
  features: any;
  index: number;
}

interface IFeature {
  type: string;
  id: string;
  geometry: Geometry;
  geometry_name: string;
  properties: Map<string, string | number>;
}

interface IFeatureDetails {
  type: string;
  features: IFeature[];
}

export function FeaturesModal({ setIsOpen, features, index }: IFeaturesModal) {
  const { map, addedLayers } = useMap();
  const { getLayerAttritubeById } = useLayers();

  const vectorLayerRef = useRef<VectorLayer<Feature<Geometry>>>();
  const geoJsonFormatRef = useRef(new GeoJSON());

  useEffect(() => {
    return () => deleteOverlay();
  }, [])

  useEffect(() => {
    if (!map) return;

    const vectorSource = new VectorSource();

    const vectorLayer = new VectorLayer({
      source: vectorSource,
      zIndex: 999,
    });

    vectorLayer.set("id", "modalSelected");
    map.addLayer(vectorLayer);

    vectorLayerRef.current = vectorLayer;
  }, [map]);

  // eslint-disable-next-line
  const getFeatureEntryByIndex = () => {
    if (!features || features.size === 0) return null;

    const entries = [...features.entries()].flatMap(
      ([key, arr]) => arr.map((feature: any) => ({ key, feature }))
    );

    return entries[index] ?? null;
  };

  // eslint-disable-next-line
  const renderFeaturesProperties = () => {
    const entry = getFeatureEntryByIndex();
    if (!entry) return;

    const { key, feature } = entry;

    if (!feature) return;
    if (!feature.properties) return setIsOpen(false);

    const isUserLayer = key === 'user_layers';

    const id = isUserLayer ? undefined : key.split('&&')[1];

    const userLayer = isUserLayer
      ? addedLayers.find(layer => layer.layertitle === entry.feature.layername)
      : undefined;

    const layer = isUserLayer ? {
          layertitle: entry.feature.layername,
          atributos: userLayer
            ? getVectorLayerAttributes(userLayer)
            : Object.entries(entry.feature.properties)
                .filter(([key]) => key !== 'geometry')
                .map(([key]) => ({
                  ds_tipo: 'Texto',
                  nm_alias: '',
                  nm_coluna: key,
                  visivel: true,
                })),
        }
      : getLayerAttritubeById(Number(id));

    if (!layer) return;

    return (
      <div className="information">
        <h4>
          <strong>{layer.layertitle}</strong>
        </h4>
        {layer.atributos
          .filter((attr: IAtributo) => {
            if (!attr.visivel) return false;
            return !isAnalytesAttribute(attr);
          })
          .map((attr: IAtributo, index: number) => {
            let value;

            // Valor utilizado para o modal de Detalhes
            if (attr.nm_coluna === 'ds_processo_grp_min') {
              return;
            }

            if (feature.properties[attr.nm_coluna] && feature.properties[attr.nm_coluna] != ' ') {
              switch (attr.ds_tipo) {
                case 'Texto':
                  if (isValidURL(feature.properties[attr.nm_coluna])) {
                    value = (
                      <a href={feature.properties[attr.nm_coluna]} target="blank">
                        Mais info
                      </a>
                    );
                  } else {
                    value = feature.properties[attr.nm_coluna];
                  }
                  break;
                case 'Número':
                  value = feature.properties[attr.nm_coluna].toLocaleString('pt-BR');
                  break;
                case 'Data':
                  value = formatDate(feature.properties[attr.nm_coluna]);
                  break;
              }
            } else {
              value = 'Não informado';
            }

            return (
              <p key={`${id}_${index}`}>
                <strong>{attr.nm_alias ? attr.nm_alias : attr.nm_coluna}: </strong>
                <span className={shouldApplyNotranslate(attr) ? 'notranslate' : ''}>
                  {value}
                </span>
              </p>
            );
          })}
      </div>
    );
  };

  function isValidURL(url: string): boolean {
    const urlPattern = new RegExp(/^(http:\/\/|https:\/\/|www\.)/);

    return !!urlPattern.test(url);
  }

  function formatDate(dateString: string): string {
    const date = new Date(dateString);

    const day = date.getUTCDate();
    const month = date.getUTCMonth() + 1;
    const year = date.getUTCFullYear();

    const dayString = day < 10 ? `0${day}` : day.toString();
    const monthString = month < 10 ? `0${month}` : month.toString();

    return `${dayString}/${monthString}/${year}`;
  }

  function clearOverlay() {
    vectorLayerRef.current?.getSource()?.clear()
  }

  function deleteOverlay() {
    if (!map) return;

    const layer = map.getLayers().getArray().find(l => 
      l.get("id") === "modalSelected"
    );

    layer && map.removeLayer(layer);
  }

  function drawGeometry(geometry: any) {
    const source = vectorLayerRef.current?.getSource();
    if (!source || !map) return;

    // Limpa o overlay anterior
    clearOverlay();

    if (!geometry) return;

    if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
      const dotFeature = new Feature({
        geometry: new Point(geometry.type === 'Point' ? geometry.coordinates : geometry.coordinates[0]),
      });

      dotFeature.setStyle(
        new Style({
          image: new Circle({
            radius: 6,
            fill: new Fill({ color: '#FFFF00' }),
            stroke: new Stroke({
              color: '#FFFF00',
              width: 3,
            }),
          }),
        }),
      );
      source.addFeature(dotFeature);
    } else {
      if (geometry.type === 'MultiPolygon') {
        // eslint-disable-next-line
        geometry.coordinates.forEach((feat: any) => {
          const lineFeature = new Feature({
            geometry: new LineString(feat[0]),
          });

          lineFeature.setStyle(
            new Style({
              stroke: new Stroke({
                color: '#FFFF00',
                width: 5,
              }),
            }),
          );

          source.addFeature(lineFeature);
        });
      } else {
        const lineFeature = new Feature({
          geometry: new LineString(geometry.coordinates[0]),
        });

        lineFeature.setStyle(
          new Style({
            stroke: new Stroke({
              color: '#FFFF00',
              width: 5,
            }),
          }),
        );
        source.addFeature(lineFeature);
      }
    }
  }

  function getFeatureGeometry(feature: any) {
    const geometry = feature?.geometry;
    if (!geometry) return null;

    if (typeof geometry.getType === 'function') {
      return geoJsonFormatRef.current.writeGeometryObject(geometry, {
        featureProjection: 'EPSG:3857',
        dataProjection: 'EPSG:3857',
      });
    }

    return geometry;
  }

  async function zoomToCoordinate(featureParam: any) {
    if (!map) return;

    const entry = getFeatureEntryByIndex();
    const feature = entry?.feature ?? featureParam;
    if (!feature) return;

    const geometry = getFeatureGeometry(feature);
    if (!geometry) return;
    
    if (geometry.type === 'Point' || geometry.type === 'MultiPoint') {
      const coord = geometry.type === 'Point'
      ? geometry.coordinates
      : geometry.coordinates[0];
      
      map.getView().setCenter(coord);
      if ((map.getView().getZoom() ?? 10) < 10) {
        map.getView().setZoom(10);
      }
    } else {
      // Create an OpenLayers geometry from your raw coords
      let olGeom;
      if (geometry.type === 'MultiPolygon') {
        olGeom = new MultiPolygon(geometry.coordinates);
      } else {
        olGeom = new Polygon(geometry.coordinates);
      }
      
      map.getView().fit(olGeom.getExtent(), {
        padding: [50, 500, 50, 500],
      });
    }

    if (entry?.key === 'user_layers') {
      drawGeometry(geometry);
      return;
    }

    const featureTypeName = entry?.key?.split('&&')[0] || feature.id?.split('.')?.[0];
    const featureid = entry?.key?.split('&&')[0] || feature.id?.split('.')?.[1];
    if (!featureTypeName || !featureid) return;
    const layer = getLayerAttritubeById(Number(featureid));

    if (!layer) return;

    const featureDetails: any = await getFeature<IFeatureDetails>(layer.host_url, featureTypeName, feature.properties);

    if (!featureDetails?.features?.length) return;

    drawGeometry(featureDetails.features[0].geometry);
  }

  return (
    <div className="content">
      <>{renderFeaturesProperties()}</>
      <a
        href="#"
        onClick={(event) => {
          event.preventDefault();
          zoomToCoordinate([...features.values()].flat()[index]);
        }}
      >
        Zoom para
      </a>
    </div>
  );
}
